import { Button, ButtonGroup } from '@mui/material'
import type { FC } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import IcBaselineChevronLeft from '~icons/ic/baseline-chevron-left'
import IcBaselineChevronRight from '~icons/ic/baseline-chevron-right'
import type { PageInfo } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { useHash } from 'src/utils/router/useHash'

export type OnCursorChange = (args: { before?: string; after?: string }) => any

export interface BaseCursorPagingButtonProps {
  pageInfo?: PageInfo
  isPosSeq?: boolean
  onChange?: OnCursorChange
  btnGroupClassName?: string
  className?: string
  onClickPrev?: () => void
  onClickNext?: () => void
  /** Use hash params instead of query params for cursor */
  preferHashParams?: boolean
}

const CursorPagingButton: FC<BaseCursorPagingButtonProps> = ({
  pageInfo,
  isPosSeq,
  className,
  btnGroupClassName,
  onChange,
  onClickPrev,
  onClickNext,
  preferHashParams = false,
}) => {
  const { t } = useTranslation(['action'])
  const [queryParams] = useSearchParams()
  const [hash] = useHash()

  // When the page stores cursor + filter state in the URL hash, build the
  // prev/next href off the current hash params so opening the link in a new
  // tab (or copying it) preserves the rest of the filter state (sort,
  // visibility, etc.). Otherwise fall back to query-string params.
  const baseParams = useMemo(
    () =>
      preferHashParams
        ? new URLSearchParams(hash.replace(/^#/, ''))
        : new URLSearchParams(queryParams),
    [preferHashParams, hash, queryParams],
  )

  const disabledPrev = isPosSeq
    ? !pageInfo?.hasPreviousPage
    : !pageInfo?.hasNextPage
  const disabledNext = isPosSeq
    ? !pageInfo?.hasNextPage
    : !pageInfo?.hasPreviousPage

  const prevLink = useMemo(() => {
    const prevParams = new URLSearchParams(baseParams)
    if (isPosSeq) {
      prevParams.delete('after')
      prevParams.set('before', pageInfo?.startCursor ?? '')
    } else {
      prevParams.delete('before')
      prevParams.set('after', pageInfo?.endCursor ?? '')
    }
    return preferHashParams
      ? `#${prevParams.toString()}`
      : `?${prevParams.toString()}`
  }, [baseParams, isPosSeq, pageInfo, preferHashParams])

  const nextLink = useMemo(() => {
    const nextParams = new URLSearchParams(baseParams)
    if (isPosSeq) {
      nextParams.delete('before')
      nextParams.set('after', pageInfo?.endCursor ?? '')
    } else {
      nextParams.delete('after')
      nextParams.set('before', pageInfo?.startCursor ?? '')
    }
    return preferHashParams
      ? `#${nextParams.toString()}`
      : `?${nextParams.toString()}`
  }, [baseParams, isPosSeq, pageInfo, preferHashParams])

  return (
    <div className={cx('flex justify-center m-8', className)}>
      <ButtonGroup className={cx(btnGroupClassName)}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabledPrev}
          href={prevLink}
          onClick={ev => {
            ev.preventDefault()
            onClickPrev?.()
            onChange?.(
              isPosSeq
                ? {
                    before: pageInfo?.startCursor,
                    after: undefined,
                  }
                : {
                    before: undefined,
                    after: pageInfo?.endCursor,
                  },
            )
          }}
          className="w-24"
          startIcon={<IcBaselineChevronLeft className="size-6" />}
        >
          <span className="capitalize">{t('action:previous')}</span>
        </Button>

        <Button
          variant="outlined"
          size="small"
          disabled={disabledNext}
          href={nextLink}
          onClick={ev => {
            ev.preventDefault()
            onClickNext?.()
            onChange?.(
              isPosSeq
                ? {
                    after: pageInfo?.endCursor,
                    before: undefined,
                  }
                : {
                    after: undefined,
                    before: pageInfo?.startCursor,
                  },
            )
          }}
          className="w-24"
          endIcon={<IcBaselineChevronRight className="size-6" />}
        >
          <span className="capitalize">{t('action:next')}</span>
        </Button>
      </ButtonGroup>
    </div>
  )
}

export default CursorPagingButton
